import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import App from './App.vue'
import vuetify from './plugins/vuetify'
import routes from './routes'
import copy from './utils/copy'
import OntosBase from './components/shared/Base.vue'
import PageHeader from './components/shared/PageHeader.vue'
import HeaderAction from './components/shared/HeaderAction.vue'
import DialogActions from './components/shared/DialogActions.vue'
import FormDialog from './components/shared/FormDialog.vue'
import Dialog from './components/shared/Dialog.vue'
import storage from '@/storage'
import {applyLanguage} from '@/utils/language'
import utils from "@/utils/utils"
import arrayUtils from "@/utils/arrayUtils"
import ontosUtils from "@/utils/ontosUtils"
import AuthService from "@/services/AuthService";
import SystemService from "@/services/SystemService";
import InstanceService from "@/services/InstanceService";
import IdeaService from "@/services/IdeaService";
import IdeaPropertyService from "@/services/IdeaPropertyService";
import WorkflowService from "@/services/WorkflowService";
import DomainService from "@/services/DomainService";
import MemberService from "@/services/MemberService";
import InvitationService from "@/services/InvitationService";
import SearchService from "@/services/SearchService";
import {isApprover} from "@/types/Access";
import i18n, {MessageKey, TranslationValues, tc} from "@/i18n";
import { auth, persistenceReady } from "@/firebase";
import firebase from "firebase/app";

// Promise that resolves once the first onAuthStateChanged callback fires,
// indicating Firebase has finished restoring the persisted auth state.
// Exported so the route guard and other modules can await it.
let resolveAuthReady: (user: firebase.User | null) => void;
export const authReady: Promise<firebase.User | null> = new Promise(resolve => {
    resolveAuthReady = resolve;
});

let authReadyResolved = false;

// Keep the listener active for the entire app lifecycle so that
// auth.currentUser always reflects the latest auth state (token refreshes, sign-outs, etc.)
auth.onAuthStateChanged(user => {
    if (!authReadyResolved) {
        authReadyResolved = true;
        resolveAuthReady(user);
    }
});

const router = createRouter({ history: createWebHistory(), routes })
//Sem o callback next: o vue-router 4+ o deu por obsoleto e avisa no console a cada
//navegação. Quem decide agora é o RETORNO — um destino desvia, e nada nenhum segue.
router.beforeEach(async (to) => {
    // Wait for Firebase to finish restoring the persisted auth state
    // before making any redirect decisions. The promise resolves once
    // and stays resolved, so subsequent navigations proceed immediately.
    await authReady;

    const user = auth.currentUser;

    if (!to.meta.public && !user) return {name: 'login'};
    //A administração do lunedi — hoje, os convites. Aqui e não no leaveIfDenied do App.vue,
    //que só roda dentro de um ambiente: uma rota de aplicação nunca passaria por lá. Quem
    //responde é o mesmo isApprover que o menu do avatar lê, para os dois não discordarem.
    if (to.meta.approver && !isApprover(storage.user)) return {name: 'home'};
    //O nome é obrigatório na conta (decisão do usuário): é por ele, e não pelo e-mail — que o modo
    //de uso nem recebe —, que os outros veem a pessoa nos ambientes. O cadastro já o pede; quem
    //entrou antes disso, ou por um provedor sem nome, vai ao perfil até preenchê-lo
    if (!to.meta.public && to.name !== 'profile' && storage.user && !storage.user.name) return {name: 'profile'};
});

//O idioma, já na partida: o preferido da conta guardada neste navegador, ou o do navegador — nos
//textos, no Vuetify, no moment (o que faz "esta semana" começar no dia certo) e no `lang` da página.
//O `sync` logo abaixo o reaplica se a conta trouxer outro. Ver `utils/language`.
applyLanguage(storage.user?.language)

async function bootstrap() {
    // Wait for explicit persistence configuration to complete
    await persistenceReady;

    // Wait for Firebase to restore the persisted auth state
    const signed = await authReady;

    const authService = new AuthService()
    /**
     * Relê quem é o usuário desta sessão, a cada abertura.
     *
     * Duas coisas de uma vez. **Repõe** a cópia quando a sessão sobreviveu e ela não: quem
     * mantém alguém logado é o Firebase, em armazenamento próprio, e o nome que a barra mostra
     * é uma cópia no localStorage que só nascia dentro do `login()` — limpar os dados do site
     * deixava a pessoa logada, sem avatar e sem o "Sair". E **mantém em dia** o que mudou do
     * lado de fora: a admissão da conta vem nessa resposta, e ela se altera sem a pessoa fazer
     * nada — alguém a promove a aprovadora, ou a devolve a pendente. Lida de uma cópia velha,
     * o menu de convites apareceria para quem não aprova mais, ou faltaria a quem passou a
     * aprovar.
     *
     * **Esperado, e não solto em segundo plano**: o `storage` é um objeto comum sobre o
     * localStorage, sem reatividade, e uma escrita depois da primeira pintura não redesenharia
     * nem o avatar nem o menu.
     *
     * **Falha em silêncio**: o aplicativo funciona sem essa cópia, e deixar de abrir porque o
     * servidor não respondeu seria trocar um avatar por uma tela branca. Quem foi devolvido a
     * pendente descobre na primeira tela, pelo `leftOut` do `RouteComponent`.
     */
    if (signed) {
        try {
            await authService.sync()
        } catch (err) {
            console.warn('Não foi possível reler o usuário desta sessão', err)
        }
    }

    const app = createApp(App)

    //Em Vue 2 isto era Vue.prototype; o alvo agora é o globalProperties da instância da
    //aplicação. Os tipos continuam declarados em types/Vue.d.ts, agora sobre a interface
    //ComponentCustomProperties.
    app.config.globalProperties.$storage = storage
    app.config.globalProperties.$copy = copy
    app.config.globalProperties.$utils = utils
    app.config.globalProperties.$arrays = arrayUtils
    app.config.globalProperties.$ontos = ontosUtils
    app.config.globalProperties.$auth = authService
    app.config.globalProperties.$systems = new SystemService()
    app.config.globalProperties.$instances = new InstanceService()
    app.config.globalProperties.$ideas = new IdeaService()
    app.config.globalProperties.$ideaProperties = new IdeaPropertyService()
    app.config.globalProperties.$workflows = new WorkflowService()
    app.config.globalProperties.$domains = new DomainService()
    app.config.globalProperties.$members = new MemberService()
    app.config.globalProperties.$invitations = new InvitationService()
    app.config.globalProperties.$search = new SearchService()
    app.config.globalProperties.$tt = function (key: MessageKey, values?: TranslationValues) {
        return i18n.global.t(key as string, values as never);
    }
    app.config.globalProperties.$tc = tc

    //Globais como o ontos-base: são usados em praticamente toda tela, e registrar
    //localmente significaria repetir import e registro em ~25 arquivos.
    app.component('ontos-base', OntosBase)
    app.component('ontos-page-header', PageHeader)
    app.component('ontos-header-action', HeaderAction)
    app.component('ontos-dialog-actions', DialogActions)
    app.component('ontos-form-dialog', FormDialog)
    app.component('ontos-dialog', Dialog)

    app.use(router)
    app.use(vuetify)
    app.use(i18n)

    app.mount('#app')
}

bootstrap();
